<template>
  <section class="admin-content">
    <Navbar />
    <main class="admin-main">

      <!-- ── Page Header ─────────────────────────────────────── -->
      <div class="page-header">
        <div class="container">
          <div class="d-flex align-items-center p-t-40 p-b-40" style="gap: 16px;">
            <div class="header-icon-wrap">
              <i class="fe fe-file-text text-white" style="font-size: 1.4rem;"></i>
            </div>
            <div class="text-white">
              <h2 class="page-title mb-1">Encounter Details</h2>
              <p class="mb-0" style="opacity: .75; font-size: .9rem;"
                v-if="encounterDetails && encounterDetails.healthrecord">
                <i class="fe fe-hash mr-1"></i>{{ encounterDetails.healthrecord.encounter_id }}
              </p>
            </div>
            <div class="ml-auto d-flex align-items-center" style="gap: 8px;">
              <span v-if="encounterDetails && encounterDetails.is_confirmed == true"
                class="header-status header-status--active">
                <i class="fe fe-check-circle mr-1"></i> Confirmed
              </span>
              <span v-if="encounterDetails && encounterDetails.is_confirmed == false"
                class="header-status header-status--inactive">
                <i class="fe fe-x-circle mr-1"></i> Rejected
              </span>
            </div>
          </div>
        </div>
      </div>

      <div class="vld-parent">
        <loading :active.sync="isLoading" loader="dots" :can-cancel="true" :is-full-page="fullPage"></loading>
      </div>

      <div class="container mt-n3 pb-5" id="printDiv" ref="printNow">

        <!-- ── Action Bar ──────────────────────────────────────── -->
        <div class="card action-card shadow mb-4">
          <div class="card-body py-3 px-4">
            <div class="d-flex flex-wrap align-items-center" style="gap: 8px;">
              <button class="btn btn-sm btn-outline-secondary" @click="printMe">
                <i class="fe fe-printer mr-1"></i> Print
              </button>
              <router-link :to="`/authorization-code/${encounterDetails.authcode.id}`"
                v-if="encounterDetails && encounterDetails.authcode">
                <button class="btn btn-sm btn-outline-danger">
                  <i class="mdi mdi-file-document-outline mr-1"></i> View Referral
                </button>
              </router-link>
            </div>
          </div>
        </div>

        <!-- ── Profile + Encounter ─────────────────────────────── -->
        <div class="row">

          <!-- Enrollee Profile -->
          <div class="col-md-4 mb-4">
            <div class="card shadow-sm enc-card h-100">
              <div class="enc-card-header">
                <i class="fe fe-user text-primary mr-2"></i>
                <span class="font-weight-bold" style="font-size: .85rem;">Enrollee Profile</span>
              </div>

              <div class="card-body text-center pb-2 pt-4">
                <!-- Registered Photo -->
                <div class="photo-label">Registered Photo</div>
                <component :is="enrolleeLink ? 'router-link' : 'div'" :to="enrolleeLink" class="enrollee-link"
                  title="Open patient page">
                  <img
                    :src="`${encounterDetails && encounterDetails.enrollee ? encounterDetails.enrollee.image_url : ''}`"
                    alt="Enrollee Photo" class="enrollee-photo mb-2"
                    onerror="this.onerror=null; this.src='/assets/img/placeholder.svg'" />
  
                  <h5 class="font-weight-bold text-dark mb-0" v-if="encounterDetails && encounterDetails.enrollee">
                    {{ encounterDetails.enrollee.full_name }}
                  </h5>
                </component>
              </div>

              <!-- Captured image at visit -->
              <div
                v-if="encounterDetails && encounterDetails.healthrecord && encounterDetails.healthrecord.enrollee_image"
                class="captured-image-section">
                <div class="photo-label captured-label">
                  <i class="fe fe-camera mr-1"></i> Captured at Visit
                </div>
                <img :src="`https://claimify-api.hayokmedicare.ng/${encounterDetails.healthrecord.enrollee_image}`"
                  alt="Captured at Visit" class="enrollee-photo mt-1"
                  onerror="this.onerror=null; this.src='/assets/img/placeholder.svg'" />
              </div>

              <div class="list-group list-group-flush mt-2" v-if="encounterDetails && encounterDetails.enrollee">
                <div class="info-row">
                  <i class="fe fe-hash info-icon"></i>
                  <div>
                    <p class="info-label">Insurance ID</p>
                    <p class="info-value">{{ encounterDetails.enrollee.id_card_number }}</p>
                  </div>
                </div>
                <div class="info-row">
                  <i class="fe fe-phone info-icon"></i>
                  <div>
                    <p class="info-label">Phone</p>
                    <p class="info-value">{{ encounterDetails.enrollee.phone_number }}</p>
                  </div>
                </div>
                <div class="info-row">
                  <i class="fe fe-gift info-icon"></i>
                  <div>
                    <p class="info-label">Date of Birth</p>
                    <p class="info-value">{{ encounterDetails.enrollee.dob }}</p>
                  </div>
                </div>
                <div class="info-row">
                  <i class="fe fe-users info-icon"></i>
                  <div>
                    <p class="info-label">Gender</p>
                    <p class="info-value">{{ encounterDetails.enrollee.gender }}</p>
                  </div>
                </div>
                <div class="info-row">
                  <i class="fe fe-layers info-icon"></i>
                  <div>
                    <p class="info-label">Sector / Plan</p>
                    <p class="info-value">{{ encounterDetails.enrollee.sector }}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Encounter Details -->
          <div class="col-md-8 mb-4">
            <div class="card shadow-sm enc-card h-100">
              <div class="enc-card-header">
                <i class="fe fe-activity text-primary mr-2"></i>
                <span class="font-weight-bold" style="font-size: .85rem;">Encounter Details</span>
              </div>

              <div v-if="encounterDetails && encounterDetails.services" class="list-group list-group-flush">
                <div class="info-row">
                  <i class="fe fe-hash info-icon"></i>
                  <div>
                    <p class="info-label">Encounter ID</p>
                    <p class="info-value">{{ encounterDetails.healthrecord.encounter_id }}</p>
                  </div>
                </div>
                <div class="info-row">
                  <i class="fe fe-tag info-icon"></i>
                  <div>
                    <p class="info-label">Encounter Type</p>
                    <p class="info-value">{{ encounterDetails.treatment_type }}</p>
                  </div>
                </div>
                <div class="info-row" v-if="encounterDetails.diagnosis">
                  <i class="fe fe-alert-circle info-icon"></i>
                  <div>
                    <p class="info-label">Diagnosis</p>
                    <p class="info-value">
                      {{ encounterDetails.diagnosis.name }}
                      <span v-for="diag in encounterDetails.other_diagnoses" :key="diag.id">, {{ diag.name }}</span>
                    </p>
                  </div>
                </div>
                <div class="info-row">
                  <i class="fe fe-message-circle info-icon"></i>
                  <div>
                    <p class="info-label">Reason for Visit</p>
                    <p class="info-value">{{ encounterDetails.healthrecord.reasonVisit }}</p>
                  </div>
                </div>
                <div class="info-row" v-if="encounterDetails.healthrecord.testResult">
                  <i class="fe fe-clipboard info-icon"></i>
                  <div>
                    <p class="info-label">Test Result</p>
                    <p class="info-value">{{ encounterDetails.healthrecord.testResult }}</p>
                  </div>
                </div>
                <div class="info-row" v-if="encounterDetails.healthrecord.notes">
                  <i class="fe fe-edit-3 info-icon"></i>
                  <div>
                    <p class="info-label">Notes</p>
                    <p class="info-value">{{ encounterDetails.healthrecord.notes }}</p>
                  </div>
                </div>
                <div class="info-row" v-if="encounterDetails.healthrecord.family_history">
                  <i class="fe fe-users info-icon"></i>
                  <div>
                    <p class="info-label">Family History, Allergies &amp; Others</p>
                    <p class="info-value">{{ encounterDetails.healthrecord.family_history }}</p>
                  </div>
                </div>
                <div class="info-row" v-if="encounterDetails.healthrecord.history_taking">
                  <i class="fe fe-file-text info-icon"></i>
                  <div>
                    <p class="info-label">History Taking</p>
                    <p class="info-value">{{ encounterDetails.healthrecord.history_taking }}</p>
                  </div>
                </div>
                <div class="info-row" v-if="encounterDetails.healthrecord.examination">
                  <i class="fe fe-search info-icon"></i>
                  <div>
                    <p class="info-label">Physical Examination</p>
                    <p class="info-value">{{ encounterDetails.healthrecord.examination }}</p>
                  </div>
                </div>
                <div class="info-row">
                  <i class="fe fe-clock info-icon"></i>
                  <div>
                    <p class="info-label">Date of Visit</p>
                    <p class="info-value">{{ encounterDetails.healthrecord.date_of_visit }}</p>
                  </div>
                </div>
                <div class="info-row" v-if="encounterDetails.provider">
                  <i class="fe fe-home info-icon"></i>
                  <div>
                    <p class="info-label">Health Facility</p>
                    <p class="info-value">{{ encounterDetails.provider.agency_name }}</p>
                  </div>
                </div>
                <div class="info-row" v-if="encounterDetails.healthrecord.professional">
                  <i class="fe fe-user-check info-icon"></i>
                  <div>
                    <p class="info-label">Desk Officer</p>
                    <p class="info-value">{{ encounterDetails.healthrecord.professional.full_name }}</p>
                    <p class="text-muted mb-0" style="font-size:.8rem;">
                      {{ encounterDetails.healthrecord.professional.phone_number }}
                    </p>
                  </div>
                </div>
              </div>

              <div v-else class="card-body text-center text-muted py-5">
                <i class="fe fe-file-text" style="font-size: 2rem;"></i>
                <p class="mt-2 mb-0">No Encounter Records</p>
              </div>
            </div>
          </div>
        </div>

        <!-- ── Vital Signs ─────────────────────────────────────── -->
        <div class="card shadow-sm enc-card mb-4"
          v-if="encounterDetails && encounterDetails.healthrecord && encounterDetails.healthrecord.vital">
          <div class="enc-card-header">
            <i class="fe fe-activity text-primary mr-2"></i>
            <span class="font-weight-bold" style="font-size: .85rem;">Vital Signs</span>
            <small class="text-muted ml-auto">
              Recorded {{ encounterDetails.healthrecord.vital.created_at | moment("MMM Do YYYY") }}
            </small>
          </div>
          <div class="card-body px-4 pt-4 pb-3">
            <div class="row">

              <div class="col-6 col-md-4 col-lg-3 mb-3">
                <div class="vital-tile vital-tile--red">
                  <i class="fe fe-thermometer vital-icon text-danger"></i>
                  <div>
                    <p class="vital-value">{{ encounterDetails.healthrecord.vital.temperature }}
                      <span class="vital-unit">°C</span>
                    </p>
                    <p class="vital-label">Temperature</p>
                  </div>
                </div>
              </div>

              <div class="col-6 col-md-4 col-lg-3 mb-3">
                <div class="vital-tile vital-tile--yellow">
                  <i class="fe fe-heart vital-icon text-warning"></i>
                  <div>
                    <p class="vital-value">{{ encounterDetails.healthrecord.vital.pulse_rate }}
                      <span class="vital-unit">bpm</span>
                    </p>
                    <p class="vital-label">Pulse Rate</p>
                  </div>
                </div>
              </div>

              <div class="col-6 col-md-4 col-lg-3 mb-3">
                <div class="vital-tile vital-tile--blue">
                  <i class="fe fe-wind vital-icon text-info"></i>
                  <div>
                    <p class="vital-value">{{ encounterDetails.healthrecord.vital.respiratory_rate }}
                      <span class="vital-unit">/min</span>
                    </p>
                    <p class="vital-label">Respiratory</p>
                  </div>
                </div>
              </div>

              <div class="col-6 col-md-4 col-lg-3 mb-3">
                <div class="vital-tile vital-tile--purple">
                  <i class="fe fe-droplet vital-icon" style="color: #9c27b0;"></i>
                  <div>
                    <p class="vital-value">{{ encounterDetails.healthrecord.vital.blood_glucose }}
                      <span class="vital-unit">mmol/L</span>
                    </p>
                    <p class="vital-label">Blood Glucose</p>
                  </div>
                </div>
              </div>

              <div class="col-12 col-md-8 col-lg-6 mb-3">
                <div class="vital-tile vital-tile--yellow">
                  <i class="fe fe-bar-chart-2 vital-icon text-warning"></i>
                  <div class="flex-grow-1">
                    <p class="vital-label mb-1">Blood Pressure</p>
                    <div class="d-flex align-items-baseline" style="gap: 12px;">
                      <div>
                        <span class="vital-value">{{ encounterDetails.healthrecord.vital.systolic }}</span>
                        <span class="vital-unit"> mmHg</span>
                        <p class="vital-label mb-0">Systolic</p>
                      </div>
                      <span class="text-muted font-weight-bold" style="font-size: 1.2rem;">/</span>
                      <div>
                        <span class="vital-value">{{ encounterDetails.healthrecord.vital.diastolic }}</span>
                        <span class="vital-unit"> mmHg</span>
                        <p class="vital-label mb-0">Diastolic</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div class="col-6 col-md-4 col-lg-2 mb-3">
                <div class="vital-tile vital-tile--green">
                  <i class="fe fe-user vital-icon text-success"></i>
                  <div>
                    <p class="vital-value" style="font-size: 1.1rem;">{{ encounterDetails.healthrecord.vital.weight }}
                      <span class="vital-unit">kg</span>
                    </p>
                    <p class="vital-label">Weight</p>
                  </div>
                </div>
              </div>

              <div class="col-6 col-md-4 col-lg-2 mb-3">
                <div class="vital-tile vital-tile--green">
                  <i class="fe fe-arrow-up vital-icon text-success"></i>
                  <div>
                    <p class="vital-value" style="font-size: 1.1rem;">{{ encounterDetails.healthrecord.vital.height }}
                      <span class="vital-unit">cm</span>
                    </p>
                    <p class="vital-label">Height</p>
                  </div>
                </div>
              </div>

              <div class="col-6 col-md-4 col-lg-2 mb-3">
                <div class="vital-tile vital-tile--green">
                  <i class="fe fe-percent vital-icon text-success"></i>
                  <div>
                    <p class="vital-value" style="font-size: 1.1rem;">{{ encounterDetails.healthrecord.vital.bmi }}</p>
                    <p class="vital-label">BMI</p>
                  </div>
                </div>
              </div>

            </div>
          </div>
        </div>

        <!-- ── Services / Drugs ──────────────────────────────── -->
        <div class="card shadow-sm enc-card mb-4"
          v-if="encounterDetails && encounterDetails.services && encounterDetails.services.length">
          <div class="enc-card-header">
            <i class="fe fe-thermometer text-primary mr-2"></i>
            <span class="font-weight-bold" style="font-size: .85rem;">Services &amp; Drugs Administered</span>
            <span class="badge badge-soft-info ml-auto">{{ encounterDetails.services.length }} item(s)</span>
          </div>
          <div class="table-responsive">
            <table class="table align-td-middle mb-0">
              <thead style="background:#f8fafc;">
                <tr>
                  <th class="table-th">#</th>
                  <th class="table-th">Type</th>
                  <th class="table-th">Name</th>
                  <th class="table-th">Dose</th>
                  <th class="table-th">Frequency</th>
                  <th class="table-th">Days / Qty</th>
                  <th class="table-th">Unit Cost</th>
                  <th class="table-th">Total Cost</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(svc, index) in encounterDetails.services" :key="svc.id" class="table-row">
                  <td class="px-3 py-3">{{ index + 1 }}</td>
                  <td class="py-3">
                    <span v-if="svc.service_type === 'Service'" class="badge badge-soft-info"
                      style="font-size: .72rem;">Service</span>
                    <span v-else class="badge badge-soft-warning" style="font-size: .72rem;">Drug</span>
                  </td>
                  <td class="py-3">
                    <span v-if="svc.service">{{ svc.service.description }}</span>
                    <span v-else-if="svc.drug">{{ svc.drug.drug_name }}</span>
                    <small v-if="svc.drug && svc.drug.dosage" class="d-block text-muted">
                      {{ svc.drug.dosage }}{{ svc.drug.presentation ? ' · ' + svc.drug.presentation : '' }}
                    </small>
                  </td>
                  <td class="py-3">
                    <span v-if="svc.service_type === 'Drug'">{{ svc.dose }}</span>
                    <span v-else class="text-muted">—</span>
                  </td>
                  <td class="py-3">{{ svc.frequency }}</td>
                  <td class="py-3">{{ svc.days }}</td>
                  <td class="py-3">
                    <span v-if="svc.service">&#8358;{{ svc.service.price | numeral(0, 0) }}</span>
                    <span v-else-if="svc.drug">&#8358;{{ svc.drug.price | numeral(0, 0) }}</span>
                  </td>
                  <td class="py-3 font-weight-bold">&#8358;{{ svc.total_cost | numeral(0, 0) }}</td>
                </tr>
              </tbody>
              <tfoot>
                <tr style="background: #f8fafc;">
                  <td colspan="7" class="px-3 py-3 text-right font-weight-bold text-dark">Total</td>
                  <td class="py-3 font-weight-bold text-dark">&#8358;{{ totalServiceCharge | numeral(0, 0) }}</td>
                </tr>
                <tr style="background: #fff8e1;">
                  <td colspan="7" class="px-3 py-2 text-right text-warning font-weight-bold" style="font-size: .82rem;">
                    10% Cost-Sharing (Drugs)
                  </td>
                  <td class="py-2 font-weight-bold text-warning" style="font-size: .82rem;">
                    &#8358;{{ tenPercent | numeral(0, 0) }}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>

        <!-- Authorization Code Modal -->
        <div class="modal fade" id="example_01" tabindex="-1" role="dialog" aria-labelledby="example_02"
          aria-hidden="true">
          <div class="modal-dialog modal-dialog-centered modal-lg" role="document">
            <div class="modal-content" style="border-radius: 12px; overflow: hidden;">
              <div class="modal-header" style="background: linear-gradient(135deg,#1e3a5f,#2563eb);">
                <div class="d-flex align-items-center">
                  <div class="mr-3 d-flex align-items-center justify-content-center"
                    style="width:36px;height:36px;background:rgba(255,255,255,.15);border-radius:9px;">
                    <i class="fe fe-lock text-white"></i>
                  </div>
                  <h5 class="modal-title mb-0 text-white" id="example_02">Authorization Code Request</h5>
                </div>
                <button type="button" class="close text-white" data-dismiss="modal" aria-label="Close">
                  <span aria-hidden="true">&times;</span>
                </button>
              </div>
              <div class="modal-body">
                <div class="form-group">
                  <label class="small font-weight-bold text-muted text-uppercase">
                    <i class="fe fe-home mr-1"></i> Select Secondary Facility
                  </label>
                  <v-select v-model="referred_to_facility" label="agency_name" :options="providers"
                    placeholder="Search facility..." />
                </div>
              </div>
              <div class="modal-footer">
                <button type="button" class="btn btn-outline-secondary" data-dismiss="modal">
                  <i class="fe fe-x mr-1"></i> Cancel
                </button>
                <button class="btn btn-primary" @click="createAuthCode">
                  <i class="fe fe-send mr-1"></i> Submit
                </button>
              </div>
            </div>
          </div>
        </div>

      </div>
    </main>
  </section>
</template>

<script>
  import Navbar from "@/views/Navbar.vue";
  import Loading from "vue-loading-overlay";
  import "vue-loading-overlay/dist/vue-loading.css";
  import { patientLink } from "@/utils/patientLink";
  let agency = JSON.parse(localStorage.getItem("agency"));

  export default {
    components: {
      Loading,
      Navbar,
    },
    data() {
      return {
        agency: agency,
        encounterDetails: "",
        patient: "",
        dependants: [],
        isLoading: false,
        encounterId: "",
        providers: "",
        referred_to_facility: "",
        fullPage: true,
        payment_type: "online",
        selectedPaymentOption: "",
        paymentOptions: { online: "online", offline: "offline" },
      };
    },
    computed: {
      enrolleeLink() {
        return patientLink(this.encounterDetails && this.encounterDetails.enrollee);
      },
      totalServiceCharge() {
        let total =
          this.encounterDetails.total_drug_price +
          this.encounterDetails.total_service_price;
        return total;
      },
      tenPercent() {
        let total = this.encounterDetails.total_drug_price;
        let calc = total * 0.1;
        return calc;
      },
    },

    methods: {
      getUserEncounterDetails() {
        this.isLoading = true;
        this.axios
          .get(`/api/v1/auth/service_summary/${this.$route.params.id}`)
          .then((response) => {
            this.encounterDetails = response.data;
            console.log(this.encounterDetails);
          })
          .catch((error) => {
            console.error(error);
          });
        this.isLoading = false;
      },

      dependentChar(index) {
        return String.fromCharCode("A".charCodeAt(0) + index);
      },
      createAuthCode() {
        if (confirm("Are you sure you want to submit request")) {
          this.isLoading = true;
          this.axios
            .post("/api/v1/auth/authorization_code", {
              agency_id: agency.id,
              principal_id: this.encounterDetails.client_id,
              dependent_id: this.encounterDetails.dependent_id,
              provider_id: this.encounterDetails.provider_id,
              referred_to_facility: this.referred_to_facility.id,
              service_summary_id: this.encounterDetails.id,
            })
            .then((response) => {
              console.log(response);
              this.isLoading = false;
              this.getUserEncounterDetails();
              this.$toasted.info(`${response.data.message}`, {
                position: "top-center",
                duration: 3000,
              });
            })
            .catch((error) => {
              console.log(error.response);
              this.isLoading = false;
              this.$breadstick.notify("Oops! something went wrong", {
                position: "top-right",
              });
            });
        }
      },
      getProviders() {
        this.axios
          .get(`/api/v1/auth/providerAgency/${agency.id}`)
          .then((response) => {
            this.providers = response.data.data;
            console.log(response);
          })
          .catch((error) => {
            console.error(error);
          });
      },

      createClaim() {
        if (confirm("Are you Sure you want to create Claim?")) {
          this.isLoading = true;
          this.axios
            .post(`/api/v1/auth/createClaimfromEncounter`, {
              encounter_id: this.$route.params.id,
            })
            .then((response) => {
              console.log(response);
              this.$toasted.info("Created Successfully!", {
                position: "top-center",
                duration: 3000,
              });
              this.getUserEncounterDetails();
            })
            .catch((error) => {
              console.error(error);
              this.$toasted.error("Error!", {
                position: "top-center",
                duration: 3000,
              });
            });
          this.isLoading = false;
        }
      },
      printMe() {
        var printContents = document.getElementById("printDiv").innerHTML;
        var originalContents = document.body.innerHTML;
        document.body.innerHTML = printContents;
        window.print();
        document.body.innerHTML = originalContents;
      },
    },

    created() {
      this.getUserEncounterDetails();
      this.getProviders();
    },
  };
</script>

<style scoped>
.enrollee-link { display: block; text-decoration: none; }
.enrollee-link:hover h5 { color: #2563eb !important; text-decoration: underline; }

  /* ── Page Header ─────────────────────────────────────── */
  .page-header {
    background: linear-gradient(135deg, #1e3a5f 0%, #2563eb 100%);
    padding-bottom: 60px;
  }

  .page-title {
    font-size: 1.5rem;
    font-weight: 700;
    margin-bottom: 0;
  }

  .header-icon-wrap {
    width: 52px;
    height: 52px;
    background: rgba(255, 255, 255, 0.15);
    border-radius: 14px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }

  .header-status {
    display: inline-flex;
    align-items: center;
    font-size: 0.78rem;
    padding: 4px 12px;
    border-radius: 20px;
    font-weight: 600;
  }

  .header-status--active {
    background: rgba(16, 185, 129, 0.25);
    color: #6ee7b7;
  }

  .header-status--inactive {
    background: rgba(239, 68, 68, 0.25);
    color: #fca5a5;
  }

  /* ── Cards ───────────────────────────────────────────── */
  .action-card {
    border-radius: 14px;
    border: none;
  }

  .enc-card {
    border-radius: 14px;
    border: none;
    overflow: hidden;
  }

  .enc-card-header {
    display: flex;
    align-items: center;
    padding: 14px 20px;
    background: #f8f9fa;
    border-bottom: 1px solid #f1f5f9;
    font-size: 0.85rem;
  }

  /* ── Enrollee photos ─────────────────────────────────── */
  .enrollee-photo {
    width: 130px;
    height: 130px;
    object-fit: cover;
    border-radius: 12px;
    border: 3px solid #e2e8f0;
    display: block;
    margin: 0 auto;
  }

  .photo-label {
    font-size: 0.7rem;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: #94a3b8;
    font-weight: 700;
    margin-bottom: 6px;
  }

  .captured-image-section {
    border-top: 1px dashed #e2e8f0;
    margin-top: 12px;
    padding: 16px;
    text-align: center;
  }

  .captured-label {
    color: #2563eb;
  }

  /* ── Info Rows ───────────────────────────────────────── */
  .info-row {
    display: flex;
    align-items: flex-start;
    gap: 12px;
    padding: 11px 20px;
    list-style: none;
    border-bottom: 1px solid #f1f5f9;
  }

  .info-row:last-child {
    border-bottom: none;
  }

  .info-icon {
    color: #2563eb;
    font-size: 0.95rem;
    margin-top: 2px;
    flex-shrink: 0;
  }

  .info-label {
    font-size: 0.7rem;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: #94a3b8;
    margin-bottom: 1px;
    font-weight: 600;
  }

  .info-value {
    font-weight: 600;
    color: #1e293b;
    font-size: 0.88rem;
    margin-bottom: 0;
  }

  /* ── Vital tiles ─────────────────────────────────────── */
  .vital-tile {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 14px;
    border-radius: 10px;
    height: 100%;
  }

  .vital-tile--red {
    background: #fff5f5;
    border: 1px solid #ffd6d6;
  }

  .vital-tile--yellow {
    background: #fff8e1;
    border: 1px solid #ffe082;
  }

  .vital-tile--blue {
    background: #e8f4fd;
    border: 1px solid #b3d9f7;
  }

  .vital-tile--purple {
    background: #f3e5f5;
    border: 1px solid #d7b8e8;
  }

  .vital-tile--green {
    background: #e8f5e9;
    border: 1px solid #a5d6a7;
  }

  .vital-icon {
    font-size: 1.5rem;
    line-height: 1;
    flex-shrink: 0;
  }

  .vital-value {
    font-weight: 700;
    font-size: 1.2rem;
    line-height: 1.2;
    margin-bottom: 0;
  }

  .vital-unit {
    font-size: 0.75rem;
    font-weight: 400;
    color: #94a3b8;
  }

  .vital-label {
    font-size: 0.7rem;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: #94a3b8;
    margin-bottom: 0;
    font-weight: 600;
  }

  /* ── Table ───────────────────────────────────────────── */
  .table-th {
    font-size: 0.75rem;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: #64748b;
    font-weight: 600;
    border-bottom: 2px solid #e2e8f0;
    padding: 12px;
  }
</style>